import CheckIcon from "@mui/icons-material/Check";
import { Button } from "@mui/material";
import { Spin } from "antd";
import React, { useContext, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { PsLabel } from "../../components";
import PsModalWindow from "../../components/PsModalWindow";
import PsContext from "../../context";
import { useDialog } from "../../context/DialogContext";
import { _LIST_SCHOOLS, _SAVE_SCHOOL } from "../../models/schoolsModel";
import { formToObject, lowerCase, SAAS_SITE_URL } from "../../utils";
import { RegionDropdown } from "react-country-region-selector";

const AddSchoolFormComponent = (props) => {
  const context = useContext(PsContext);
  const [loader, setLoader] = useState(false);

  const { openDialog } = useDialog();

  const [validated, setValidated] = useState(false);
  const [schoolUrl, setSchoolUrl] = useState("");
  const [schoolData, setSchoolData] = useState([]);
  const [isUrlValid, setIsUrlValid] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [selectedState, setSelectedState] = useState("Tamil Nadu");
  const [price, setPrice] = useState("");

  const validatePrice = (value) => {
    let num = parseInt(value, 10);
    if (isNaN(num)) return ""; // Allow empty field
    if (num < 60) return 60; // Minimum limit
    if (num > 120) return 120; // Maximum limit
    return num;
  };

  const handleValidateUrl = () => {
    if (!schoolUrl.trim()) {
      openDialog({
        title: "Validation Error",
        message: "Please enter a school URL before validating.",
      });
      return;
    }

    setIsValidating(true);
    _LIST_SCHOOLS({ school_prefix: schoolUrl, for: "all" })
      .then((res) => {
        setValidationAttempted(true); // Mark validation as attempted
        if (res.status === "1") {
          setIsUrlValid(res.data.length === 0); // True if URL is available
          setSchoolData(res.data);
        } else {
          setIsUrlValid(true);
        }
        setIsValidating(false);
      })
      .catch((error) => {
        setIsValidating(false);
        openDialog({
          title: "Validation Error",
          message: "Failed to validate URL. Please try again.",
        });
      });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    setLoader(true);
    let formObj = formToObject(form);
    _SAVE_SCHOOL(formObj).then((res) => {
      if (res.status == "1") {
        form.reset();
        //context.setAlertMsg(res.message || "Saved");
        openDialog({
          title: "School Created",
          message: (
            <>
              The school <b className="text-uppercase">{formObj.school_name}</b>{" "}
              has been created successfully. You can access it using the
              following URL.
              <br />
              <span className="bg-green-50 border p-1  my-2">
                {context.userData.domain_name
                  ? context.userData.domain_name
                  : "school.kalvisalai.in"}
                /{formObj.school_prefix}
              </span>
              <br />
              Please use the credentials below to log in.
              <br />
              Username : <b>{res.data.username || ""}</b>
              <br />
              Password : <b>{res.data.password || ""}</b>
              <br />
            </>
          ),
        });
        if (props.onSuccess) props.onSuccess();
      } else {
        openDialog({
          title: "Error",
          message: res.message || "Error on Save",
        });
      }
      setLoader(false);
    });
  };

  const handlePrefixBlur = (e) => {
    let v = e.target.value;
    if (v && v.length > 0) {
      let str = v.replace(/[^a-zA-Z0-9_-]/g, "");
      if (str) e.target.value = lowerCase(str);
    }
    return;
  };

  /*  const handleSchoolUrlChange = (e) => {
    setSchoolUrl(e.target.value);
    setIsUrlValid(false); // Reset URL validation
    setValidationAttempted(false); // Reset validation attempt status
  };
 */

  const handleSchoolUrlChange = (e) => {
    const inputValue = e.target.value;

    // Allow only alphanumeric characters and hyphens (optional)
    const sanitizedValue = inputValue.replace(/[^a-zA-Z0-9-]/g, ""); // Removes spaces & special characters

    setSchoolUrl(sanitizedValue);
    setIsUrlValid(false); // Reset URL validation
    setValidationAttempted(false); // Reset validation attempt status
  };

  return (
    <>
      <Spin spinning={loader}>
        <Form
          method="post"
          noValidate
          validated={validated}
          onSubmit={handleFormSubmit}
        >
          <Row className="mt-2">
            <Col md={3}>
              <Form.Label className="fw-bold">School URL</Form.Label>
            </Col>
            <Col md={6}>
              <Form.Control
                type="text"
                name="school_prefix"
                placeholder="Allowed hyphen (-) & underscore (_) only"
                size="sm"
                className="fw-bold text-lowercase"
                autoFocus
                autoComplete="off"
                onBlur={handlePrefixBlur}
                onChange={handleSchoolUrlChange} // ✅ Reset validation if changed
                required
              />
            </Col>
            <Col md={3}>
              <Button
                variant="contained"
                size="small"
                onClick={handleValidateUrl}
                disabled={!schoolUrl.trim() || isValidating || isUrlValid}
              >
                {isValidating ? "Validating..." : "Validate URL"}
              </Button>
            </Col>
          </Row>

          {/* Show validation result */}
          {validationAttempted && (
            <div
              className={`my-2 p-2 border rounded ${
                isUrlValid
                  ? "bg-success-light text-success"
                  : "bg-danger-light text-danger"
              }`}
            >
              {`Your url : ${
                context.userData.domain_name
                  ? context.userData.domain_name
                  : "school.kalvisalai.in"
              }/`}
              {schoolUrl}
              <span className="ms-2 fw-semibold">
                {isUrlValid ? "✔ Available" : "✖ Not Available"}
              </span>
            </div>
          )}
          <fieldset disabled={!isUrlValid}>
            <Row className="mt-3">
              <Col md={3}>
                <PsLabel text="School Name" required />
              </Col>
              <Col md={9}>
                <Form.Control
                  type="text"
                  name="school_name"
                  placeholder="School Name"
                  size="sm"
                  className="fw-bold text-uppercase"
                  autoComplete="off"
                  required
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={3}>
                <PsLabel text="School Address" required />
              </Col>
              <Col md={9}>
                <Form.Control
                  as="textarea"
                  name="school_address"
                  placeholder="School Address"
                  size="sm"
                  rows={4}
                  className="fw-bold"
                  autoComplete="off"
                  required
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={3}>
                <PsLabel text="Place & City" required />
              </Col>
              <Col md={4}>
                <Form.Control
                  type="text"
                  name="school_place"
                  placeholder="Place"
                  size="sm"
                  className="fw-bold"
                  autoComplete="off"
                  required
                />
              </Col>
              <Col md={5}>
                <Form.Control
                  type="text"
                  name="school_city"
                  placeholder="City"
                  size="sm"
                  className="fw-bold"
                  autoComplete="off"
                  required
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={3}>
                <PsLabel text="State & Pincode" required />
              </Col>
              <Col md={4}>
                <RegionDropdown
                  className="form-select form-select-sm fw-bold"
                  name="school_state"
                  country="India"
                  value={selectedState}
                  onChange={(val) => setSelectedState(val)}
                  required="required"
                />
              </Col>
              <Col md={5}>
                <Form.Control
                  type="tes"
                  name="school_pincode"
                  placeholder="Pincode"
                  size="sm"
                  className="fw-bold"
                  maxLength={6}
                  autoComplete="off"
                  onWheel={(e) => e.target.blur()}
                  required
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={3}>
                <PsLabel text="Mobile & Email Id" required />
              </Col>
              <Col md={4}>
                <Form.Control
                  type="number"
                  name="school_mobile"
                  placeholder="Mobile Number"
                  size="sm"
                  className="fw-bold"
                  autoComplete="off"
                  onWheel={(e) => e.target.blur()}
                  required
                />
              </Col>

              <Col md={5}>
                <Form.Control
                  type="email"
                  name="school_email"
                  size="sm"
                  placeholder="Email Id"
                  className="fw-bold"
                  autoComplete="off"
                  required
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={3}>
                <PsLabel text="School Type & Website" />
              </Col>
              <Col md={4}>
                <Form.Control
                  as="select"
                  name="school_type"
                  size="sm"
                  className="fw-bold form-select form-select-sm"
                  required
                >
                  <option value="matriculation">Matriculation</option>
                  <option value="government">Government</option>
                  <option value="cbse">CBSE</option>
                </Form.Control>
              </Col>
              <Col md={5}>
                <Form.Control
                  type="url"
                  name="school_website"
                  size="sm"
                  className="fw-bold"
                  placeholder="School Website"
                  autoComplete="off"
                  defaultValue="https://"
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={3}>
                <PsLabel text="Price Setup (₹) & Status" required />
              </Col>
              <Col md={4}>
                <Form.Control
                  type="number"
                  name="price_setup"
                  placeholder="Enter price (₹)"
                  size="sm"
                  className="fw-bold"
                  autoComplete="off"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  onBlur={() => setPrice(validatePrice(price))}
                />
              </Col>
              <Col md={5}>
                <Form.Control
                  as="select"
                  name="active_status"
                  size="sm"
                  className="fw-bold form-select form-select-sm"
                  required
                >
                  <option value="1">Active</option>
                  <option value="0">In-Active</option>
                </Form.Control>
              </Col>

              {/* HINT BOX - Moves Below Inputs */}
              <Col md={12} className="mt-2">
                <div className="p-2 border rounded bg-light text-dark shadow-sm">
                  <strong>💡 Price Setup:</strong> Your cost is
                  <strong> ₹48</strong> per student. You can set a price between
                  <strong> ₹60</strong> and <strong> ₹120</strong> per student
                  per year.
                </div>
              </Col>
            </Row>

            {/* <Row className="mt-3">
              <Col md={3}>
                <PsLabel text="Status" required />
              </Col>
              <Col md={9}>
                <Form.Control
                  as="select"
                  name="active_status"
                  size="sm"
                  className="fw-bold form-select form-select-sm"
                  required
                >
                  <option value="1">Active</option>
                  <option value="0">In-Active</option>
                </Form.Control>
              </Col>
            </Row> */}

            <Row className="mt-3">
              <Col md={12}>
                <div className="text-end">
                  <a
                    href="javascript:;"
                    className="me-3 text-decoration-underline"
                    onClick={(e) => props.onHide && props.onHide()}
                  >
                    Cancel
                  </a>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<CheckIcon />}
                    size="small"
                    disabled={!isUrlValid}
                  >
                    Save
                  </Button>
                </div>
              </Col>
            </Row>
          </fieldset>
        </Form>
      </Spin>
    </>
  );
};
const AddSchoolForm = (props) => {
  return (
    <>
      <PsModalWindow size="lg" title="Add School" {...props}>
        {props.show && <AddSchoolFormComponent {...props} />}
      </PsModalWindow>
    </>
  );
};
export default AddSchoolForm;
